import { NavLink } from "react-router-dom";
import styled from "styled-components";

const Container = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 5rem;
`;

const ContainerBox = styled.div`
  width: 31.25rem;
  max-width: 90vw;
  border-radius: 0.5rem;
  box-shadow: 0px 4px 20px 0px ${({ theme }) => theme.BLACK_SHADOW};
  background-color: ${({ theme }) => theme.WHITE};
  overflow: hidden;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0px 6px 25px 0px ${({ theme }) => theme.BLACK_SHADOW};
  }

  @media (max-width: 768px) {
    width: 100%;
    margin: 0 1rem;
  }
`;

const Title = styled.h1`
  background-color: ${({ theme }) => theme.BLUE_1};
  padding: 1rem;
  font-size: 1.25rem;
  color: ${({ theme }) => theme.WHITE};
`;
const InputFieldWrapper = styled.div`
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.WHITE_2};
  position: relative;
`;
const Link = styled(NavLink)`
  color: ${({ theme }) => theme.BLUE_3};
  font-size: 1rem;
  padding: 6px 1rem;
  margin: 0 0.5rem;
  border-radius: 10px;
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.BLUE};
    text-decoration: underline;
  }
`;
const DontRememberLinkSection = styled.div`
  margin-top: 3rem;
`;
const DontRememberLink = styled(Link)`
  position: absolute;
  text-align: end;
  margin-top: 23px ;
  width:95%;
  z-index : 1;
`;
const BackToHomeLinkWrap = styled.div`
  padding: 2rem 0 1rem 0;
  border: 1px solid ${({ theme }) => theme.WHITE_2};
  position: relative;
`;
const BackToHomeLink = styled(Link)`
  text-align: center;
  width: 100%;
  display: block;
`;

// OTP Verification Screen
const OtpSubHeading = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.GRAY_2};
  margin: 0.25rem 0 0.25rem 0;
  line-height: 1.5;
`;

const OtpEmail = styled.strong`
  color: ${({ theme }) => theme.BLACK_1};
  word-break: break-all;
`;

const OtpInputRow = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
  margin: 1.5rem 0 0.75rem 0;
`;

const OtpDigit = styled.input<{ hasError?: boolean }>`
  flex: 1;
  min-width: 0;
  height: 3.25rem;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.BLACK_1};
  border: 1px solid
    ${({ theme, hasError }) => (hasError ? theme.RED_1 : theme.WHITE_2)};
  border-radius: 0.375rem;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: ${({ theme }) => theme.BLUE_6};
    box-shadow: 0 0 0 3px rgba(8, 124, 167, 0.15);
  }

  &:disabled {
    background-color: ${({ theme }) => theme.WHITE_1};
    cursor: not-allowed;
  }

  /* hide number spinners */
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  @media (max-width: 480px) {
    height: 2.75rem;
    font-size: 1.15rem;
  }
`;

const OtpTimerText = styled.p<{ expired?: boolean }>`
  font-size: 0.875rem;
  text-align: center;
  margin: 0;
  color: ${({ theme, expired }) => (expired ? theme.RED_1 : theme.GRAY_2)};
`;

const OtpTrustLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 1.25rem;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.BLACK_1};
  cursor: pointer;

  input {
    margin-top: 0.2rem;
    cursor: pointer;
  }
`;

const OtpFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 1.25rem;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.GRAY_2};
`;

const OtpLinkButton = styled.button.attrs({ type: "button" })`
  background: none;
  border: none;
  padding: 0;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.BLUE_3};
  cursor: pointer;
  text-decoration: none;

  &:hover:enabled {
    text-decoration: underline;
  }

  &:disabled {
    color: ${({ theme }) => theme.GRAY_1};
    cursor: not-allowed;
  }
`;

// Notice shown while an account is locked out after too many attempts
const LockoutNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: #842029;
  background-color: #f8d7da;
  border: 1px solid #f1aeb5;
  border-radius: 0.375rem;
  padding: 0.875rem 1rem;
  margin: 0 0 1rem 0;
`;

const LockoutIcon = styled.span`
  display: flex;
  font-size: 1.15rem;
  line-height: 1.4;
  flex-shrink: 0;
`;

const LockoutTitle = styled.p`
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
`;

const LockoutText = styled.p`
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
`;

const LockoutCountdown = styled.p`
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  font-size: 0.875rem;
  margin: 0.5rem 0 0 0;
`;

const LockoutTimer = styled.strong`
  font-size: 1rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
`;

const LockoutHint = styled.p`
  font-size: 0.8rem;
  line-height: 1.5;
  margin: 0.4rem 0 0 0;
  opacity: 0.85;
`;

// Way out of the lockout - inherits the notice's colour so it reads as part of it
const LockoutAction = styled.button.attrs({ type: "button" })`
  background: none;
  border: none;
  padding: 0;
  margin-top: 0.6rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

// Create Password Screen
const PasswordContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 5rem;
`;

const PasswordBox = styled.div`
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.WHITE_2};
  position: relative;
`;
const PasswordLinKBox = styled.div`
  padding: 2rem 0 1rem 0;
  border: 1px solid ${({ theme }) => theme.WHITE_2};
  position: relative;
`;
const PasswordHeader = styled.h1`
  background-color: ${({ theme }) => theme.BLUE_4};
  padding: 1rem;
  font-size: 1.25rem;
  color: ${({ theme }) => theme.WHITE};
`;

export {
  Container,
  ContainerBox,
  Title,
  InputFieldWrapper,
  Link,
  DontRememberLinkSection,
  DontRememberLink,
  BackToHomeLinkWrap,
  BackToHomeLink,
  OtpSubHeading,
  OtpEmail,
  OtpInputRow,
  OtpDigit,
  OtpTimerText,
  OtpTrustLabel,
  OtpFooter,
  OtpLinkButton,
  LockoutNotice,
  LockoutIcon,
  LockoutTitle,
  LockoutText,
  LockoutCountdown,
  LockoutTimer,
  LockoutHint,
  LockoutAction,
  PasswordHeader,
  PasswordContainer,
  PasswordBox,
  PasswordLinKBox,
};
